import {
  fork,
  takeLatest,
  call,
  put,
  all,
  select,
} from "redux-saga/effects";
import {
  resetTimer,
  resetSubmitResult,
  setTimeLeft,
  fetchExamInstructionRequest,
  fetchExamInstructionSuccess,
  fetchExamInstructionFailure,
  fetchExamQuestionRequest,
  fetchExamQuestionSuccess,
  fetchExamQuestionFailure,
  fetchExamQuestionsListRequest,
  fetchExamQuestionsListSuccess,
  fetchExamQuestionsListFailure,
  applyResumeSnapshot,
  saveAnswerRequest,
  saveAnswerSuccess,
  saveAnswerFailure,
  setCurrentQuestionIndex,
  setActiveSection,
  fetchPracticeListSuccess,
  fetchPracticeListFailure,
  fetchPracticeListRequest,
  submitExamRequest,
  submitExamSuccess,
  submitExamFailure,
  saveBookmarkRequest,
  saveBookmarkSuccess,
  saveBookmarkFailure,
  getPyqYearWiseSuccess,
  getPyqYearWiseFailure,
  getPyqYearWiseRequest,
  getPyqYearwisePapersSuccess,
  getPyqYearwisePapersFailure,
  getPyqYearwisePapersRequest,
  fetchSubjectListSuccess,
  fetchSubjectListFailure,
  fetchSubjectListRequest,
  getPyqTopicWiseSuccess,
  getPyqTopicWiseFailure,
  getPyqTopicWiseRequest,
  getPyqSubjectWiseSuccess,
  getPyqSubjectWiseFailure,
  getPyqSubjectWiseRequest,
  getPyqTopicWiseSetsSuccess,
  getPyqTopicWiseSetsFailure,
  getPyqTopicWiseSetsRequest,
  fetchPracticeSummaryRequest,
  fetchPracticeSummarySuccess,
  fetchPracticeSummaryFailure,
  setTestDetails,
  setPracticeStreak,
  resetPracticeStreak,
  hidePracticeProgressiveMessage,
} from "./examSlice";
import {
  clearBasicAnalytics,
} from "../exam_analytics/analyticsSlice";

import examApi from "./examApi";
import { getExamSectionKey } from "../../shared/utilities/helpers/examSectionKey";

/* ------------------ FETCH INSTRUCTION ------------------ */
function* fetchExamInstructionWorker(action) {
  try {
    const response = yield call(examApi.getInstruction, action.payload);
    yield put(fetchExamInstructionSuccess(response.data));
  } catch (error) {
    yield put(fetchExamInstructionFailure(error.message));
  }
}

/* ------------------ FETCH SINGLE QUESTION ------------------ */
function* fetchExamQuestionWorker(action) {
  try {
    const { testType, testDetails, questionsData } = yield select(
      (state) => state.examSlice
    );

    const question = questionsData?.find((q) => q.qid === action.payload.qid);

    const response = yield call(examApi.getQuestion, {
      paperType: action.payload?.paperType ?? testType?.id,
      paperId:
        action.payload?.paperId ??
        action.payload?.paper_id ??
        question?.paper_id ??
        testDetails?.paperId,
      qid: action.payload.qid,
      testId: action.payload?.testId ?? testDetails?.testId,
      setId: action.payload?.setId ?? testDetails?.setId,
      plannerTestId: testDetails?.planner_test_id ?? null,
      adaptive_question_id: action.payload?.adaptive_question_id,
      focus_set_no: action.payload?.focus_set_no,
    });

    yield put(fetchExamQuestionSuccess(response.data));
  } catch (error) {
    yield put(fetchExamQuestionFailure(error.message));
  }
}

/* ------------------ FETCH QUESTIONS LIST ------------------ */
function* fetchExamQuestionsListWorker(action) {
  try {
    yield put(resetSubmitResult());

    // const { testDetails } = yield select((state) => state.examSlice);

    const response = yield call(examApi.getQuestionList, {
      ...action.payload,
      practiceMode: action.payload?.practiceMode,
      gamification_status:
        action.payload?.practiceMode === "practice" && action.payload?.gamificationOn
          ? 1
          : undefined,
    });

    const preState = yield select((state) => state.examSlice);
    const currentTestId =
      action.payload?.adaptive_question_id ||
      action.payload?.setId ||
      action.payload?.testId ||
      action.payload?.paperId;
    const resumeStateKey = `exam_resume_state_${currentTestId}`;
    const storedResumeState = localStorage.getItem(resumeStateKey);
    let parsedResumeSnapshot = null;
    if (storedResumeState) {
      try {
        const parsed = JSON.parse(storedResumeState);
        if (
          parsed?.snapshotVersion === 2 &&
          parsed?.testKey &&
          String(parsed.testKey) === String(currentTestId)
        ) {
          parsedResumeSnapshot = parsed;
        }
      } catch (_) {
        parsedResumeSnapshot = null;
      }
    }

    // Explicit forceFreshStart on this fetch = Start / Reattempt → always wipe.
    // Page refresh passes forceFreshStart:false and restores from local snapshot.
    const isFreshStartFlow = action.payload?.forceFreshStart === true;
    const effectiveIsResume =
      !isFreshStartFlow &&
      (action.payload?.isResume === true || parsedResumeSnapshot !== null);

    yield put(
      fetchExamQuestionsListSuccess({
        ...response?.data,
        _freshStart: isFreshStartFlow,
        _isResume: effectiveIsResume,
        _isAdaptiveResume:
          action.payload?.isResume === true &&
          action.payload?.adaptive_question_id !== undefined &&
          action.payload?.adaptive_question_id !== null,
        _resumeSnapshot: isFreshStartFlow ? null : parsedResumeSnapshot,
      })
    );
    yield put(setTimeLeft(null));
    // const paperDuration = response?.data?.paper_duration || 10800;
    const state = yield select((state) => state.examSlice);
const paperDuration = state.paper_duration || 10800;

    const latestStateForFlags = yield select((state) => state.examSlice);
    const isPractice =
      action.payload?.practiceMode === "practice" ||
      latestStateForFlags?.testDetails?.practiceMode === "practice";
    const isGamificationOn =
      action.payload?.gamificationOn === true ||
      latestStateForFlags?.testDetails?.gamificationOn === true;

    const isResumeFlow = effectiveIsResume;
    const isAdaptiveResumeFlow =
      action.payload?.isResume === true &&
      action.payload?.adaptive_question_id !== undefined &&
      action.payload?.adaptive_question_id !== null;
    const isGamifiedPractice = isPractice && isGamificationOn;
    const rawLastQuestionId = response?.data?.userGivenLastQuestionId;
    const hasBackendResumePoint =
      rawLastQuestionId !== null &&
      rawLastQuestionId !== undefined &&
      Number(rawLastQuestionId) !== 0;
    const matchesBackendLastQuestion = (q) =>
      String(q?.qid) === String(rawLastQuestionId) ||
      String(q?.id) === String(rawLastQuestionId) ||
      String(q?.question_id) === String(rawLastQuestionId) ||
      String(q?.questionId) === String(rawLastQuestionId);
    const backendResumeCursorTrusted =
      isAdaptiveResumeFlow &&
      hasBackendResumePoint &&
      (state?.questionsData || []).some(matchesBackendLastQuestion);

    // Apply local resume snapshot whenever it matches this test (same as test mode).
    // Previously, gamified practice cleared storage when the API omitted userGivenLastQuestionId,
    // which skipped applyResumeSnapshot and hid prior selections.
    let appliedResumeSnapshot = false;
    if (isFreshStartFlow) {
      localStorage.removeItem(resumeStateKey);
    } else if (parsedResumeSnapshot) {
      yield put(
        applyResumeSnapshot({
          snapshot: parsedResumeSnapshot,
          skipCursorRestore: backendResumeCursorTrusted,
        })
      );
      appliedResumeSnapshot = true;
    }

    // Fresh gamified practice (no backend pointer and no valid local snapshot): reset streak UI.
    if (isGamifiedPractice && !hasBackendResumePoint && !appliedResumeSnapshot && isFreshStartFlow ) {
      yield put(resetPracticeStreak());
      yield put(hidePracticeProgressiveMessage());
      localStorage.removeItem(resumeStateKey);
    }

    // Clear sticky forceFreshStart so a later page refresh can resume from snapshot.
    if (latestStateForFlags?.testDetails?.forceFreshStart === true) {
      yield put(
        setTestDetails({
          ...latestStateForFlags.testDetails,
          forceFreshStart: false,
        })
      );
    }

const testKey = `exam_timeLeft_${currentTestId}`;
const isPracticeMode = latestStateForFlags?.testDetails?.practiceMode === "practice";

//below isPractice method resolved issue of timer starting from 0 after resume in PYQ topicwise


if (isPracticeMode) {
  if (isResumeFlow) {
    const storedTime = localStorage.getItem(testKey);

    if (storedTime) {
      yield put(setTimeLeft(Number(storedTime)));
    } else {
      localStorage.setItem(testKey, "0");
      yield put(resetTimer(0));
    }
  } else {
    // fresh start
    localStorage.removeItem(testKey);
    localStorage.setItem(testKey, "0");
    yield put(resetTimer(0));
  }
}
else {
  if (isFreshStartFlow) {
    // localStorage.removeItem(testKey);
    localStorage.setItem(testKey, paperDuration.toString());
    yield put(resetTimer(paperDuration));
  } else {
  const storedTime = localStorage.getItem(testKey);

  if (storedTime) {
    yield put(setTimeLeft(Number(storedTime)));
  } else {
    localStorage.setItem(testKey, paperDuration.toString());
    yield put(resetTimer(paperDuration));
  }
  }
}

    const latestState = yield select((state) => state.examSlice);
    const resumeIndex = latestState?.currentQuestionIndex;

    const resumeQuestion =
  latestState?.questionsData?.[resumeIndex] ||
  response?.data?.data?.[0];

    if (resumeQuestion?.qid) {
      yield put(
        fetchExamQuestionRequest({
          qid: resumeQuestion.qid,
          paperId: resumeQuestion.paper_id,
          paperType: action.payload?.paperType,
          adaptive_question_id: action.payload?.adaptive_question_id,
          testId: currentTestId,
          setId: action.payload?.setId,
        })
      );
    }
  } catch (error) {
    yield put(fetchExamQuestionsListFailure(error.message));
  }
}

/* ------------------ SAVE ANSWER ------------------ */
function* saveAnswerWorker(action) {
  try {
    const state = yield select((state) => state.examSlice);

    const currentIndex = state.currentQuestionIndex;
    const currentQ = state.questionsData[currentIndex];
    const nextIndex = currentIndex + 1;

    const finalPayload = {
      ...action.payload,
      paper_id: currentQ?.paper_id,
      test_id: state.testDetails?.testId,
      practiceMode: state.testDetails?.practiceMode,
      gamification_status:
        state.testDetails?.practiceMode === "practice" &&
        state.testDetails?.gamificationOn
          ? 1
          : undefined,
    };

    const response = yield call(examApi.saveAnswer, finalPayload);
    yield put(saveAnswerSuccess(response));

//  STORE PRACTICE STREAK DATA (ONLY FOR PRACTICE MODE)
// Only update streak/message for instant feedback actions (`skipAdvance: true`).
// This prevents streak message re-appearing when user presses "Save & Next".
if (
  action.payload?.skipAdvance === true &&
  state.testDetails?.practiceMode === "practice" &&
  response?.question_answer_streak
) {
  yield put(setPracticeStreak(response.question_answer_streak));
}


    const shouldAdvance = action.payload?.skipAdvance !== true;

    if (shouldAdvance && nextIndex < state.questionsData.length) {
      const nextQ = state.questionsData[nextIndex];

      const getSectionKey = (q) => getExamSectionKey(q);

      if (getSectionKey(currentQ) !== getSectionKey(nextQ)) {
        yield put(setActiveSection(getSectionKey(nextQ)));
      }

      yield put(setCurrentQuestionIndex(nextIndex));

      yield put(
        fetchExamQuestionRequest({
          qid: nextQ.qid,
          paperId: nextQ.paper_id,
          testId: state.testDetails?.testId,
          setId: state.testDetails?.setId,
        })
      );
    }
  } catch (error) {
    yield put(saveAnswerFailure(error.message));
  }
}

/* ------------------ PRACTICE LIST ------------------ */
function* fetchPracticeListWorker() {
  try {
    const response = yield call(examApi.getPracticeList);
    yield put(fetchPracticeListSuccess(response.data.data));
  } catch (error) {
    yield put(fetchPracticeListFailure(error.message));
  }
}

/* ------------------ SUBMIT EXAM ------------------ */
function* submitExamWorker(action) {
  try {
    const { testDetails } = yield select((state) => state.examSlice);
    const isTopicWisePyq = !!testDetails?.setId;
    const isAdaptiveSubmit = Boolean(action.payload?.adaptive_question_id);
    let payload;

    if (isAdaptiveSubmit) {
      payload = {
        paperType: action.payload.paperType,
        adaptive_question_id: action.payload.adaptive_question_id,
        time: action.payload.time,
      };
    } else if (isTopicWisePyq) {
      payload = {
        paperType: action.payload.paperType,
        time: action.payload.time,
        setId: action.payload.setId,
        paperId: undefined,
        testId: undefined,
        plannerId: undefined,
      };
    } else {
      payload = {
        paperType: action.payload.paperType,
        paperId: action.payload.paperId,
        testId: testDetails?.testId,
        plannerId: action.payload.plannerId,
        time: action.payload.time,
      };
    }

    const response = yield call(examApi.submitExam, {
      ...payload,
      practiceMode: testDetails?.practiceMode,
      gamification_status:
        testDetails?.practiceMode === "practice" && testDetails?.gamificationOn
          ? 1
          : undefined,
    });

    yield put(submitExamSuccess(response));

    yield put(clearBasicAnalytics());
    yield put(setTimeLeft(null));

    // localStorage.removeItem("exam_timeLeft");
    // localStorage.removeItem("exam_testId");
    const currentTestId =
  testDetails?.setId ||
   testDetails?.testId ||
  testDetails?.paperId;

const testKey = `exam_timeLeft_${currentTestId}`;
localStorage.removeItem(testKey);
const resumeStateKey = `exam_resume_state_${currentTestId}`;
localStorage.removeItem(resumeStateKey);

// const timeSpentKey = `exam_timeSpent_${currentTestId}`;
// localStorage.removeItem(timeSpentKey);

// setTimeout(() => {
//   const timeSpentKey = `exam_timeSpent_${currentTestId}`;
//   localStorage.removeItem(timeSpentKey);
// }, 10000);

  } catch (error) {
    yield put(submitExamFailure(error.message));
  }
}

function* fetchPyqYearWiseWorker(action) {
  try {
    const response = yield call(examApi.getPyqYearWise, action.payload);
    yield put(getPyqYearWiseSuccess(response.data));
  } catch (error) {
    yield put(getPyqYearWiseFailure(error.message));
  }
}

// function* fetchPyqYearwisePapersWorker(action) {
//   try {
//     const response = yield call(examApi.getPyqYearwisePapers, action.payload);
//     yield put(getPyqYearwisePapersSuccess(response.data));
//   } catch (error) {
//     yield put(getPyqYearwisePapersFailure(error.message));
//   }
// }
function* fetchPyqYearwisePapersWorker(action) {
  try {
    //  Get selectedType from Redux
    const selectedType = yield select(
      (state) => state.examSlice.selectedType
    );

    //  Convert to backend target (1=JEE Main, 2=JEE Adv, 3=NEET)
    const target =
      selectedType === "adv" ? 2 : selectedType === "neet" ? 3 : 1;

    const response = yield call(examApi.getPyqYearwisePapers, {
      year: action.payload.year,
      target, //  always send correct target
    });

    yield put(getPyqYearwisePapersSuccess(response.data));
  } catch (error) {
    yield put(getPyqYearwisePapersFailure(error.message));
  }
}


function* fetchSubjectListWorker(action) {
  try {
    const response = yield call(examApi.subjectList, action.payload);
    yield put(fetchSubjectListSuccess(response));
  } catch (error) {
    yield put(fetchSubjectListFailure(error.message));
  }
}

function* saveBookmarkWorker(action) {
  try {
    const response = yield call(examApi.saveBookmark, action.payload);
    yield put(saveBookmarkSuccess(response));
  } catch (error) {
    yield put(saveBookmarkFailure(error.message));
  }
}

function* fetchPyqTopicWiseWorker(action) {
  try {
    const response = yield call(examApi.topicWise, action.payload);
    yield put(getPyqTopicWiseSuccess(response.data.subject));
  } catch (error) {
    yield put(getPyqTopicWiseFailure(error.message));
  }
}

function* fetchPyqSubjectWiseWorker(action) {
  try {
    const response = yield call(examApi.subjectWise, action.payload);
    const raw = response?.data;
    const topics = Array.isArray(raw)
      ? raw
      : raw?.subject_topics || raw?.topics || [];
    yield put(getPyqSubjectWiseSuccess(topics));
  } catch (error) {
    yield put(getPyqSubjectWiseFailure(error.message));
  }
}
function* fetchPracticeSummaryWorker(action) {
  try {
    const { paper_type, paper_id, set_id, gamification_status } =
      action.payload;

    const response = yield call(examApi.getPracticeSummary, {
      paperType: paper_type,
      paperId: paper_id,
      setId: set_id,
      gamification_status,
    });

    yield put(fetchPracticeSummarySuccess(response));
  } catch (error) {
    yield put(fetchPracticeSummaryFailure(error.message));
  }
}

function* fetchPyqTopicWiseSetsWorker(action) {
  try {
    const response = yield call(examApi.getPyqTopicWiseSets, action.payload);
    const payload =
      response?.data?.sets != null ? response.data : response?.data ?? response;
    yield put(getPyqTopicWiseSetsSuccess(payload));
  } catch (error) {
    yield put(getPyqTopicWiseSetsFailure(error.message));
  }
}

function* setTestDetailsWorker(action) {
  // New attempt: drop any prior local snapshot so Start never resumes an old run.
  if (action.payload?.forceFreshStart !== true) return;

  const testKey =
    action.payload?.adaptive_question_id ||
    action.payload?.setId ||
    action.payload?.testId ||
    action.payload?.paperId;

  if (testKey) {
    localStorage.removeItem(`exam_resume_state_${testKey}`);
  }
}

/* ------------------ ROOT SAGA ------------------ */
function* watchExamSaga() {
  yield takeLatest(
    fetchExamInstructionRequest.type,
    fetchExamInstructionWorker
  );
  yield takeLatest(fetchExamQuestionRequest.type, fetchExamQuestionWorker);
  yield takeLatest(
    fetchExamQuestionsListRequest.type,
    fetchExamQuestionsListWorker
  );
  yield takeLatest(saveAnswerRequest.type, saveAnswerWorker);
  yield takeLatest(submitExamRequest.type, submitExamWorker);
  yield takeLatest(fetchPracticeListRequest.type, fetchPracticeListWorker);
  yield takeLatest(setTestDetails.type, setTestDetailsWorker);

  yield takeLatest(getPyqYearWiseRequest.type, fetchPyqYearWiseWorker);
  yield takeLatest(
    getPyqYearwisePapersRequest.type,
    fetchPyqYearwisePapersWorker
  );
  yield takeLatest(fetchSubjectListRequest.type, fetchSubjectListWorker);
  yield takeLatest(saveBookmarkRequest.type, saveBookmarkWorker);
  yield takeLatest(getPyqTopicWiseRequest.type, fetchPyqTopicWiseWorker);
  yield takeLatest(getPyqSubjectWiseRequest.type, fetchPyqSubjectWiseWorker);
  yield takeLatest(
    getPyqTopicWiseSetsRequest.type,
    fetchPyqTopicWiseSetsWorker
  );
  yield takeLatest(
    fetchPracticeSummaryRequest.type,
    fetchPracticeSummaryWorker
  );
}

export default function* examSaga() {
  yield all([fork(watchExamSaga)]);
}